import { useCallback, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  ScrollArea,
  Text,
  TextField,
} from "@radix-ui/themes";
import FontAwesome from "../../../components/utilities/FontAwesome";
import { IOption } from "../../../components/utilities/MultiSelect";
import useTheme from "../../../components/radix/useTheme";
import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../../components/notifications/NotificationManager";
import { useStores } from "../../../stores/useStores";
import { debounce } from "../../../utils/CleversiteUtilities";

type GroupList = { category: string | null; options: IOption[] };

export function SearchableGroupSelect(props: {
  multiple: boolean;
  search: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
  options?: { [category: string]: IOption[] };
  labels?: { [id: string]: string };
  brokenIds?: string[];
}) {
  const { organizationStore } = useStores();
  const radixTheme = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [labelCache, setLabelCache] = useState<{ [id: string]: string }>({});

  const serverSearch = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const client = await WatsonApi();
        const response =
          await client.apis.organizations.organizations_permissions_search_permissions_groups(
            {
              organization_pk: organizationStore.currentOrganization.id,
              value,
            },
          );
        const parsed = JSON.parse(response.data);
        setResults(
          parsed.map((r: { id: string; title: string }) => ({
            value: r.id,
            title: r.title,
          })),
        );
      } catch (e) {
        console.error(e);
        NotificationManager.error("Internal server error. Try again later.");
      } finally {
        setLoading(false);
      }
    }, 250) as (value: string) => void,
    [],
  );

  function handleQueryChange(value: string) {
    setQuery(value);
    if (props.search) serverSearch(value);
  }

  function labelFor(id: string) {
    return props.labels?.[id] || labelCache[id] || id;
  }

  function selectValue(option: IOption) {
    setLabelCache((prev) => ({
      ...prev,
      [option.value]: option.title.trim() || option.value,
    }));
    const isSelected = props.selected.includes(option.value);
    if (props.multiple) {
      props.onChange(
        isSelected
          ? props.selected.filter((id) => id !== option.value)
          : [...props.selected, option.value],
      );
    } else {
      props.onChange(isSelected ? [] : [option.value]);
      setOpen(false);
      setQuery("");
    }
  }

  let groups: GroupList[];
  if (props.search) {
    groups = [{ category: null, options: results }];
  } else {
    const q = query.trim().toLowerCase();
    groups = Object.entries(props.options ?? {})
      .map(([category, opts]) => ({
        category,
        options: q
          ? opts.filter((o) => o.title.toLowerCase().includes(q))
          : opts,
      }))
      .filter((g) => g.options.length > 0);
  }
  const isEmpty = groups.every((g) => g.options.length === 0);
  const hasSelection = props.selected.length > 0;

  return (
    <Box width={"100%"} style={{ minWidth: 0 }}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <div
            role={"button"}
            tabIndex={0}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-1)",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
              minHeight: "var(--space-6)",
              padding: "3px var(--space-2)",
              border: "1px solid var(--gray-a7)",
              borderRadius: "var(--radius-2)",
              background: "var(--color-surface)",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "var(--space-1)",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {!hasSelection ? (
                <Text size={"2"} color={"gray"}>
                  {props.multiple ? "None selected" : "Select…"}
                </Text>
              ) : props.multiple ? (
                props.selected.map((id) => (
                  <Badge
                    key={id}
                    variant={"soft"}
                    size={"2"}
                    color={props.brokenIds?.includes(id) ? "red" : undefined}
                    title={
                      props.brokenIds?.includes(id)
                        ? "No longer found in your directory"
                        : undefined
                    }
                  >
                    {labelFor(id)}
                    <IconButton
                      type={"button"}
                      variant={"ghost"}
                      size={"1"}
                      aria-label={"Remove"}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onChange(props.selected.filter((v) => v !== id));
                      }}
                    >
                      <FontAwesome prefix={"fas"} name={"fa-times"} />
                    </IconButton>
                  </Badge>
                ))
              ) : (
                <Text size={"2"} truncate>
                  {labelFor(props.selected[0])}
                </Text>
              )}
            </div>
            <FontAwesome prefix={"fas"} name={"fa-chevron-down"} />
          </div>
        </Popover.Trigger>
        <Popover.Content
          container={radixTheme?.portalContainer?.current}
          style={{ width: "var(--radix-popper-anchor-width)", minWidth: 240 }}
        >
          <Box pb={"2"}>
            <TextField.Root
              autoFocus
              placeholder={"Search"}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
            >
              <TextField.Slot side={"left"}>
                <FontAwesome
                  prefix={"fas"}
                  name={loading ? "fa-spinner" : "fa-magnifying-glass"}
                  spin={loading}
                  fixedWidth
                />
              </TextField.Slot>
              {query && (
                <TextField.Slot side={"right"}>
                  <IconButton
                    type={"button"}
                    variant={"ghost"}
                    size={"1"}
                    color={"gray"}
                    onClick={() => handleQueryChange("")}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-times"} />
                  </IconButton>
                </TextField.Slot>
              )}
            </TextField.Root>
          </Box>
          <ScrollArea scrollbars={"vertical"} style={{ maxHeight: 240 }}>
            <Flex direction={"column"} gap={"1"} px={"2"} mr={"3"}>
              {isEmpty ? (
                <Text size={"2"} color={"gray"} align={"center"} my={"2"}>
                  {props.search && !query ? "Type to search" : "No results"}
                </Text>
              ) : (
                groups.map((g) => (
                  <Box key={g.category ?? "results"}>
                    {g.category && (
                      <Text
                        size={"1"}
                        color={"gray"}
                        weight={"medium"}
                        ml={"1"}
                      >
                        {g.category}
                      </Text>
                    )}
                    {g.options.map((o) => {
                      const isSelected = props.selected.includes(o.value);
                      return (
                        <Button
                          key={o.value}
                          type={"button"}
                          variant={"ghost"}
                          color={"gray"}
                          onClick={() => selectValue(o)}
                          // same ghost box for every row; selection only adds a
                          // background, weight, and check — never shifts layout
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                            margin: 0,
                            backgroundColor: isSelected
                              ? "var(--gray-a4)"
                              : undefined,
                          }}
                        >
                          <Text
                            size={"2"}
                            weight={isSelected ? "medium" : "regular"}
                            truncate
                          >
                            {o.title}
                          </Text>
                          {isSelected && (
                            <FontAwesome prefix={"fas"} name={"fa-check"} />
                          )}
                        </Button>
                      );
                    })}
                  </Box>
                ))
              )}
            </Flex>
          </ScrollArea>
        </Popover.Content>
      </Popover.Root>
    </Box>
  );
}
